import React, { Fragment, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { timesheetApi } from '@app/store';
import { ITimesheet } from '@app/common';
import { Button, Form, Panel } from '@app/components';
import { TimesheetForm } from './TimesheetForm';
import { fromTimesheetForm, toTimesheetForm } from './timesheet-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { TimesheetCard } from './TimesheetCard';
import { CheckPermissions } from '@app/components/CheckPermissions';

interface TimesheetUpdateProps {
  timesheet?: Partial<ITimesheet>;
  isFetching: boolean;
  toggle: (mode: 'UPDATE' | 'SHOW') => void;
  onDelete: (timesheet: Partial<ITimesheet>) => void;
}

export const TimesheetUpdate = ({ timesheet, isFetching, toggle, onDelete }: TimesheetUpdateProps) => {
  const toast = useToast();
  const triptych = useTriptych();

  const [update, { isLoading, error: apiErrors }] = timesheetApi.useUpdateOneTimesheetsMutation();

  const formMethods = useForm({
    defaultValues: toTimesheetForm(timesheet)
  });

  const onUpdate = async (data: ReturnType<typeof toTimesheetForm>) => {
    try {
      const updatedTimesheet = await update({
        id: timesheet?._id!,
        data: fromTimesheetForm(data, timesheet),
      }).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedTimesheet });

      toast.success('Le pointage a bien été modifié.');
    } catch {
      toast.error('Une erreur est survenue lors de la modification du pointage.');
    }
  };

  useEffect(() => {
    formMethods.reset(toTimesheetForm(timesheet));
  }, [timesheet, formMethods.reset]);

  return (
    <Fragment>
      {timesheet && <TimesheetCard timesheet={timesheet as ITimesheet} />}

      <Panel.Content>
        <Form formMethods={formMethods} apiErrors={apiErrors} isLoading={isFetching}>
          <Panel.FormContainer>
            <CheckPermissions permissions={['dashboard::timesheets.delete']}>
              <Panel.Header>
                <Button
                  title="Supprimer"
                  disabled={!timesheet}
                  bordered
                  important
                  onPress={() => onDelete(timesheet!)} />
              </Panel.Header>
            </CheckPermissions>

            <TimesheetForm />
          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              disabled={isFetching}
              onPress={formMethods.handleSubmit(onUpdate)}
              title="Valider"
              grow
              large />
          </Panel.Actions>
        </Form>
      </Panel.Content>
    </Fragment>
  );
};

